import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { getBetByName, getFullTimeOdds } from 'helpers/matchHelper';
import { formatDateTime } from 'helpers/dateHelper';
import { translateBetMarketName } from 'helpers/betI18n';
import { FULL_TIME_BETGROUP_NAME } from 'config/constants';
import { SlipContext } from 'context/slipContext';
import { TeamCrest } from 'components/TeamCrest/TeamCrest';
import { OtherBets } from 'components/OtherBets/OtherBets';
import { MatchInsightsModal } from 'components/MatchInsights/MatchInsightsModal';
import { MatchOffer } from 'types/MatchOffer';
import { VipIcon } from '../VipChrome/VipIcons';
import { SportIcon } from 'components/ThemeIcon/SportIcon';
import { getDefaultSportIconSrc } from 'helpers/sportIcon';
import styles from './VipMatchRow.module.css';
import { ROW_ATTR } from 'lib/rowAnchor';

/** The match's 1 X 2 as three tiles; the picked one in the theme's colour. */
export const VipMatchOdds = ({
  match,
  wide = false,
}: {
  match: MatchOffer;
  /** Over the card's whole width (the statistics page). */
  wide?: boolean;
}) => {
  const { t } = useTranslation();
  const { addSlipEntry, removeSlipEntry, entryExist } = useContext(SlipContext);
  const { homeWin, draw, awayWin } = getFullTimeOdds(match);
  const tile = (odd: typeof homeWin, label: string) => {
    if (!odd) return null;
    const on = entryExist(match, match.fullTimeBet, odd);
    return (
      <button
        type="button"
        className={classNames(styles.odd, on && styles.oddOn)}
        aria-pressed={on}
        onClick={() =>
          on
            ? removeSlipEntry(match, match.fullTimeBet, odd)
            : addSlipEntry(match, match.fullTimeBet, odd)
        }
      >
        <i>{label}</i>
        {odd.odds.toFixed(2)}
      </button>
    );
  };
  if (!homeWin && !draw && !awayWin) {
    return <span className={styles.noOffer}>{t('no_bet_offer_message')}</span>;
  }
  return (
    <div className={classNames(styles.odds, wide && styles.oddsWide)}>
      {tile(homeWin, t('bet_col_1x2_home'))}
      {tile(draw, t('bet_col_1x2_draw'))}
      {tile(awayWin, t('bet_col_1x2_away'))}
    </div>
  );
};

interface Props {
  match: MatchOffer;
  betName?: string;
  isMultiLeagueList?: boolean;
}

/** One match on one line: time and teams, the odds beside them, a way into
 *  the match's other markets. Another market is shown whole under the line. */
export const VipMatchRow = ({ match, betName, isMultiLeagueList }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showInsights, setShowInsights] = useState(false);
  const date = new Date(match.time * 1000);
  const open = () => navigate(`/sports-betting/match/${match.fi}`);
  const homeName = match.home?.name ?? match.league?.name ?? 'Unknown';
  const awayName = match.away?.name ?? '-';
  const otherMarket = !!betName && betName !== FULL_TIME_BETGROUP_NAME;
  const selectedBet = otherMarket ? getBetByName(match, betName!) : null;
  const more = match.bets.length - 1;

  return (
    // The row a list keeps in place (lib/rowAnchor).
    <div className={styles.row} {...{ [ROW_ATTR]: String(match.fi) }}>
      {showInsights && (
        <MatchInsightsModal
          fi={match.fi}
          onClose={() => setShowInsights(false)}
          heroOdds={<VipMatchOdds match={match} wide />}
        />
      )}
      <div className={styles.line}>
        <div className={styles.teams}>
          <small>
            {isMultiLeagueList && (
              <SportIcon
                className={styles.sport}
                sportId={match.league.sport}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = getDefaultSportIconSrc();
                }}
              />
            )}
            {isMultiLeagueList
              ? formatDateTime(date, { removeSeconds: true, removeYear: true })
              : `${pad(date.getHours())}:${pad(date.getMinutes())}`}
            {isMultiLeagueList && <em>{match.league.name}</em>}
            <button
              type="button"
              className={styles.stats}
              aria-label={t('match_statistics_label')}
              onClick={() => setShowInsights(true)}
            >
              <VipIcon name="chart" size={19} />
            </button>
          </small>
          <button type="button" className={styles.team} onClick={open}>
            <TeamCrest
              imageId={match.home?.image_id}
              className={styles.crest}
            />
            <span>{homeName}</span>
          </button>
          <button type="button" className={styles.team} onClick={open}>
            <TeamCrest
              imageId={match.away?.image_id}
              className={styles.crest}
            />
            <span>{awayName}</span>
          </button>
        </div>
        {!otherMarket && <VipMatchOdds match={match} />}
        <button
          type="button"
          className={styles.more}
          aria-label={t('other_bet_types_label')}
          onClick={open}
        >
          {more > 0 && `+${more}`}
          <VipIcon name="chevron" size={16} />
        </button>
      </div>
      {otherMarket &&
        (match.marketsPending ? (
          <div className={styles.note}>{t('loading_label')}</div>
        ) : selectedBet ? (
          <div className={styles.market}>
            <OtherBets bets={[selectedBet]} match={match} />
          </div>
        ) : (
          <div className={styles.note}>
            {t('no_bet_offer_message')} ·{' '}
            {translateBetMarketName({ name: betName!, odds: [] }, t)}
          </div>
        ))}
    </div>
  );
};

const pad = (n: number) => String(n).padStart(2, '0');
